// M4 — Business WEB encounter capture producer.
//
// The third implementation of ONE product behaviour. Android uses AudioRecord
// (MediaRecorder.AudioSource.VOICE_COMMUNICATION); iOS uses expo-av Audio.Recording. Both produce
// LINEAR16 mono 16 kHz from the PROVIDER'S MICROPHONE and POST it to the same M2 ingress. This does
// exactly that, from the browser, and nothing else.
//
// IT NEVER OPENS A SECOND MICROPHONE. When a call is active it already holds the provider's mic
// track — `useWebRTC` keeps it on `localStreamRef` and the comment there already says "transcript
// capture" — and that track IS the source. Calling getUserMedia then would open a second microphone
// with its own permission prompt, its own device selection and its own lifecycle, and the two could
// diverge (muted in the call, live in the recording). So when a call stream is supplied, the
// producer uses its audio track or refuses; it never reaches for a second one.
//
// WHEN THERE IS NO CALL AT ALL, it opens the provider's microphone itself. That is the
// appointment-detail case: an in-person consultation is recorded in the room, with no WebRTC
// anywhere, exactly as Android's AppointmentCaptureController and iOS's AppointmentCapture already
// do from their scheduling screens. The two cases are told apart by whether a stream was supplied —
// presence of a call stream means the call owns the microphone; absence means there is no call to
// take one from. A track this producer opened is stopped when it stops; a call's track never is.
//
// WHY NOT MediaRecorder. It is the obvious browser API and it is the wrong one here: it produces
// webm/opus, and M2 accepts only audio/l16, audio/pcm, audio/wav, audio/x-wav, audio/wave,
// audio/basic. Using it would mean either a server transcoder that exists solely for Web, or a
// second STT configuration — a second path for the same governed content. Web Audio gives raw PCM,
// so the browser matches the contract instead of the contract bending to the browser.
//
// WHAT IT DOES NOT OWN: consent, eligibility, appointment authority, WebRTC, STT, persistence. It is
// handed a decision and a track; it produces segments. Every governed question is answered elsewhere.

// ── the canonical contract, matched to the other two planes ───────────────────────────────────────
export const TARGET_SAMPLE_RATE = 16000;   // Google STT: LINEAR16 @ 16 kHz
export const TARGET_CHANNELS = 1;          // mono, as Android and iOS both send
export const SEGMENT_MS = 10000;           // ~10s, as iOS — well under the 3 MB server cap
export const CAPTURE_MIME = 'audio/l16';   // raw signed 16-bit PCM; in SUPPORTED_MIME
export const CAPTURE_TYPE_AUDIO = 'audio';
export const SOURCE_DESKTOP = 'desktop';   // capture_transport.CAPTURE_SOURCES

export const H_CAPTURE_TYPE = 'x-capture-type';
export const H_SEGMENT_ID = 'x-capture-segment-id';
export const H_SEQUENCE = 'x-capture-sequence';
export const H_DURATION = 'x-capture-duration-seconds';
export const H_STARTED = 'x-capture-started-at';
export const H_ENDED = 'x-capture-ended-at';
export const H_IS_FINAL = 'x-capture-is-final';
export const H_SOURCE = 'x-capture-source';

/**
 * The exact header set the other two planes send. Optional timestamps are omitted when unknown
 * rather than sent empty — the server would otherwise have to tell an empty string from a real one.
 */
export function buildHeaders(seg) {
  const h = {
    'Content-Type': CAPTURE_MIME,
    [H_CAPTURE_TYPE]: CAPTURE_TYPE_AUDIO,
    [H_SEGMENT_ID]: seg.segment_id,
    [H_SEQUENCE]: String(seg.sequence_number),
    [H_DURATION]: String(seg.duration_s),
    [H_IS_FINAL]: String(seg.is_final),
    [H_SOURCE]: SOURCE_DESKTOP,
  };
  if (seg.segment_started_at) h[H_STARTED] = seg.segment_started_at;
  if (seg.segment_ended_at) h[H_ENDED] = seg.segment_ended_at;
  return h;
}

/**
 * Linear resample to 16 kHz.
 *
 * Browsers run the audio graph at the hardware rate (typically 44.1 or 48 kHz); STT is configured
 * for exactly 16 kHz and does not infer it. Sending 48 kHz samples labelled 16 kHz would transcribe
 * as fast, garbled speech — audio that is wrong in a way that still *looks* like a transcript, which
 * is worse than no transcript at all.
 */
export function resampleTo16k(input, inputRate) {
  if (!input || input.length === 0) return new Float32Array(0);
  if (inputRate === TARGET_SAMPLE_RATE) return input;
  const ratio = inputRate / TARGET_SAMPLE_RATE;
  const outLength = Math.floor(input.length / ratio);
  const out = new Float32Array(outLength);
  for (let i = 0; i < outLength; i += 1) {
    const pos = i * ratio;
    const left = Math.floor(pos);
    const right = Math.min(left + 1, input.length - 1);
    const frac = pos - left;
    out[i] = input[left] * (1 - frac) + input[right] * frac;   // linear interpolation
  }
  return out;
}

/**
 * Float32 [-1, 1] -> signed 16-bit little-endian PCM.
 *
 * Byte order is explicit: `setInt16(offset, value, true)`. LINEAR16 is little-endian, and a
 * big-endian buffer decodes to noise rather than failing — so it is stated here and asserted in the
 * tests rather than inherited from whatever the platform happens to be.
 *
 * Values are clamped before scaling: a sample slightly outside [-1, 1] would otherwise wrap around
 * and turn a loud moment into a click.
 */
export function floatToPcm16(input) {
  const buf = new ArrayBuffer(input.length * 2);
  const view = new DataView(buf);
  for (let i = 0; i < input.length; i += 1) {
    const s = Math.max(-1, Math.min(1, input[i]));
    view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true /* little-endian */);
  }
  return buf;
}

/** Concatenate Float32 chunks into one buffer. */
export function concatFloat32(chunks) {
  let total = 0;
  for (const c of chunks) total += c.length;
  const out = new Float32Array(total);
  let off = 0;
  for (const c of chunks) { out.set(c, off); off += c.length; }
  return out;
}

/**
 * THE Web capture producer.
 *
 * ONE instance for the whole Business Web plane — see `webEncounterCapture` at the bottom of this
 * file. Two instances would be two audio graphs and two running-states that disagree about whether
 * this appointment is being recorded, and the surface that asked second would be the one that is
 * wrong. Android enforces this with an injected @Singleton and iOS with a shared module instance.
 *
 * `deps.send(businessId, appointmentId, body, headers)` is injected so the positive path can be
 * executed in a test with the network faked at that one seam — the shipped request shape is the part
 * that has to be right.
 */
export class EncounterCaptureProducer {
  constructor(deps = {}) {
    this.deps = deps;
    this.running = false;
    this.lastRefusal = null;
    this.segmentsSent = 0;
    this._ctx = null;
    this._source = null;
    this._node = null;
    this._chunks = [];
    this._timer = null;
    this._seq = 0;
    this._prefix = '';
    this._segStartedAt = null;
    this._inputRate = TARGET_SAMPLE_RATE;
    this._businessId = '';
    this._appointmentId = '';
    this._ownedStream = null;   // a mic WE opened, and must therefore close
    // Surfaces subscribe rather than poll. Scheduling detail and the call surface are mounted at
    // different moments, and a surface that opens while a capture is already running has to show
    // "recording" immediately — not whenever its next render happens to coincide with a change.
    this._listeners = new Set();
  }

  isRunning() { return this.running; }

  /** The appointment currently being captured, or '' when idle — so a surface can tell whether the
   *  running capture is the one IT is showing, rather than assuming any capture must be its own. */
  runningAppointmentId() { return this.running ? this._appointmentId : ''; }

  /** Observe running-state changes. Returns an unsubscribe function. */
  subscribe(listener) {
    this._listeners.add(listener);
    return () => { this._listeners.delete(listener); };
  }

  _emit() {
    for (const listener of Array.from(this._listeners)) {
      // A subscriber that throws is a rendering problem, never a reason to interrupt a recording
      // that is governed, consented and in progress.
      try { listener(this.running, this._appointmentId); } catch (e) { /* isolated */ }
    }
  }

  /**
   * Begin capture from the CALL'S EXISTING audio track, or refuse and change nothing.
   *
   * Returns true only if the graph actually started. Every refusal leaves no context, no node and no
   * timer — there is no partial state to clean up.
   */
  async start({ businessId, appointmentId, decision, operatorRequested, stream }) {
    if (this.running) return true;

    // The one gate. Not a convenience check — the ONLY path past it. `operatorRequested` records
    // that a person asked, which is what makes "no automatic capture" structural.
    if (!decision || !decision.enabled || !operatorRequested) {
      this.lastRefusal = (decision && decision.reason) || 'NOT_ELIGIBLE';
      return false;
    }
    if (!businessId || !appointmentId) {
      this.lastRefusal = 'SCOPE_REQUIRED';
      return false;
    }

    let track = null;
    if (stream) {
      // A call is active and owns the microphone.
      track = stream.getAudioTracks && stream.getAudioTracks()[0];
      if (!track) {
        // The call has no audio to capture. Refusing is correct: opening our own microphone here
        // would be a second media lifecycle the call does not know about, on a device the call has
        // already taken. Never silently substitute a different source for the one asked for.
        this.lastRefusal = 'NO_CALL_AUDIO_TRACK';
        return false;
      }
    } else {
      // No call. This is the appointment-detail capture — an in-person consultation recorded in the
      // room — so the provider's microphone is opened here, the same way both mobile planes do.
      const devices = typeof navigator !== 'undefined' && navigator.mediaDevices;
      if (!devices || !devices.getUserMedia) {
        this.lastRefusal = 'MICROPHONE_UNSUPPORTED';
        return false;
      }
      let owned = null;
      try {
        owned = await devices.getUserMedia({ audio: { channelCount: TARGET_CHANNELS } });
      } catch (err) {
        // Denied, in use, or no device. The TYPE only — never the error's device strings.
        this.lastRefusal = 'MICROPHONE_DENIED';
        return false;
      }
      track = owned && owned.getAudioTracks && owned.getAudioTracks()[0];
      if (!track) {
        try { if (owned) owned.getTracks().forEach((t) => t.stop()); } catch (e) { /* nothing open */ }
        this.lastRefusal = 'MICROPHONE_DENIED';
        return false;
      }
      this._ownedStream = owned;
    }

    const Ctx = typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext);
    if (!Ctx) {
      this.lastRefusal = 'AUDIO_CONTEXT_UNSUPPORTED';
      return false;
    }

    try {
      this._ctx = new Ctx();
      this._inputRate = this._ctx.sampleRate || TARGET_SAMPLE_RATE;
      this._source = this._ctx.createMediaStreamSource(new MediaStream([track]));
      // ScriptProcessor is deprecated but is the compatible path across the browsers this console
      // supports; an AudioWorklet needs a separately served module file, which the CSP-constrained
      // bundle does not provide. The processing is identical either way.
      this._node = this._ctx.createScriptProcessor(4096, TARGET_CHANNELS, TARGET_CHANNELS);
      this._node.onaudioprocess = (e) => {
        if (!this.running) return;
        const ch = e.inputBuffer.getChannelData(0);
        this._chunks.push(new Float32Array(ch));   // copy: the event buffer is reused
      };
      this._source.connect(this._node);
      // Connect to destination with zero gain: some browsers do not run a ScriptProcessor unless it
      // is connected to the graph's destination. Gain 0 means the provider never hears themselves.
      const mute = this._ctx.createGain();
      mute.gain.value = 0;
      this._node.connect(mute);
      mute.connect(this._ctx.destination);
    } catch (err) {
      this.lastRefusal = 'AUDIO_GRAPH_FAILED';
      await this.stop();          // also closes a microphone this call opened moments ago
      return false;
    }

    this._businessId = businessId;
    this._appointmentId = appointmentId;
    this._seq = 0;
    this._prefix = `${appointmentId}-${Date.now().toString(36)}`;
    this._segStartedAt = new Date().toISOString();
    this.running = true;
    this._timer = setInterval(() => { void this._rotate(false); }, SEGMENT_MS);
    this._emit();
    return true;
  }

  /** Flush the final segment and release everything. Idempotent. */
  async stop() {
    if (this._timer) { clearInterval(this._timer); this._timer = null; }
    const wasRunning = this.running;
    this.running = false;
    if (wasRunning) await this._rotate(true);
    try { if (this._node) { this._node.onaudioprocess = null; this._node.disconnect(); } } catch (e) { /* already gone */ }
    try { if (this._source) this._source.disconnect(); } catch (e) { /* already gone */ }
    try { if (this._ctx && this._ctx.state !== 'closed') await this._ctx.close(); } catch (e) { /* already gone */ }
    // Only a microphone THIS producer opened is closed. A call's track belongs to the call, and
    // stopping it would end the consultation's audio because the recording ended.
    try {
      if (this._ownedStream) this._ownedStream.getTracks().forEach((t) => t.stop());
    } catch (e) { /* already gone */ }
    this._ownedStream = null;
    this._node = null;
    this._source = null;
    this._ctx = null;
    this._chunks = [];
    if (wasRunning) this._emit();
  }

  /**
   * Readiness is a CONTINUOUSLY granted permission. Consent withdrawn mid-consultation, a policy
   * change, or the receiver going away all arrive as a refreshed decision and must STOP a capture in
   * progress — not merely grey out a button.
   */
  async onReadinessChanged(decision) {
    if (this.running && (!decision || !decision.enabled)) {
      this.lastRefusal = (decision && decision.reason) || 'NOT_ELIGIBLE';
      await this.stop();
    }
  }

  /** One segment: drain, resample, encode, send. Never retried, never stored. */
  async _rotate(isFinal) {
    const chunks = this._chunks;
    this._chunks = [];
    const startedAt = this._segStartedAt;
    this._segStartedAt = new Date().toISOString();
    if (!chunks.length) return;

    const raw = concatFloat32(chunks);
    const resampled = resampleTo16k(raw, this._inputRate);
    if (resampled.length === 0) return;
    const body = floatToPcm16(resampled);

    const n = this._seq;
    this._seq += 1;
    const seg = {
      segment_id: `${this._prefix}-${n}`,
      sequence_number: n,
      duration_s: resampled.length / TARGET_SAMPLE_RATE,
      segment_started_at: startedAt,
      segment_ended_at: new Date().toISOString(),
      is_final: !!isFinal,
    };

    try {
      if (this.deps.send) {
        await this.deps.send(this._businessId, this._appointmentId, body, buildHeaders(seg));
        this.segmentsSent += 1;
      }
    } catch (err) {
      // Transient; the server de-duplicates on retry and the capture loop must not stop because one
      // upload failed. The TYPE only — never the audio, never the appointment's content.
      this.lastRefusal = null;
    }
  }
}

/**
 * THE Business Web encounter capture producer.
 *
 * The appointment-detail control and the active-call control drive this one object, so a capture
 * started from Scheduling detail is already running — and reads as running — when the provider joins
 * and Unified Desktop mounts, and a stop from either surface is seen by both. Android reaches the
 * same guarantee with an injected @Singleton and iOS with a shared module instance; this is the Web
 * form of the same rule, not a Web-specific design.
 */
export const webEncounterCapture = new EncounterCaptureProducer({});

/**
 * Bind the segment transport to the shared producer.
 *
 * Both surfaces bind the SAME api on mount, so this is normally a no-op after the first call. It
 * refuses to swap the transport underneath a running capture: that capture's remaining segments
 * belong to the tenant and appointment it started with.
 */
export function bindCaptureTransport(send) {
  if (typeof send === 'function' && !webEncounterCapture.running) {
    webEncounterCapture.deps = { send };
  }
  return webEncounterCapture;
}

export default EncounterCaptureProducer;
